import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { noop } from '../lib/serviceHelpers';

// In-app meetings (2026-10-25, Calendly out): open times, booking (phone or
// RingCentral Video), and changing a meeting, through the board's routes or,
// for 'onboarding', the client's (the id is the onboardingId and the route
// resolves the client's lead).
const basePath = (mount, id) =>
  mount === 'onboarding' ? `/client-onboarding/${id}` : `/onboarding-board/leads/${id}`;

/**
 * A 7-day window of open times bucketed by CT day. `start` (YYYY-MM-DD)
 * pages by week; keepPreviousData keeps the grid painted while the next
 * week loads. retry:false: the 409s here (nobody owns the lead) are
 * answers, not transient failures.
 */
export const useBookingSlots = ({ mount, leadId, start }, opts = {}) => {
  return useQuery(
    ['booking-slots', mount, leadId, start || null],
    async () => {
      const res = await axios.get(`${basePath(mount, leadId)}/booking-slots`, {
        params: start ? { start } : {},
      });
      return res.data;
    },
    {
      enabled: !!leadId,
      keepPreviousData: true,
      staleTime: 20000,
      retry: false,
      ...opts,
    },
  );
};

// The body every booking sends: the start, the veteran's timezone (their
// email, texts and portal read in it), and for video the staff member's
// RingCentral sign-in (the meeting is created under their account).
const bookingBody = ({ startTime, timezone, video, rcAccessToken }) => ({
  startTime,
  ...(timezone ? { timezone } : {}),
  video: !!video,
  ...(video && rcAccessToken ? { rcAccessToken } : {}),
});

// Everything that shows a meeting: the boards, the Workspaces, the
// Schedule and the open times. Every change refreshes them at once (Nic
// 2026-10-26: a booking took up to a minute to reach the Schedule, which
// only refreshed on its own timer).
const refreshMeetings = (qc) => {
  qc.invalidateQueries(['client-onboarding']);
  qc.invalidateQueries(['onboarding-board']);
  qc.invalidateQueries(['onboarding-potential']);
  qc.invalidateQueries(['onboarding-meetings']);
  qc.invalidateQueries(['booking-slots']);
};

export const useBookMeeting = (callback, opts = {}) => {
  const qc = useQueryClient();
  return useMutation(
    async ({ mount, leadId, ...rest }) => {
      const res = await axios.post(`${basePath(mount, leadId)}/book-meeting`, bookingBody(rest));
      return res.data;
    },
    {
      ...opts,
      onSuccess: (...args) => {
        refreshMeetings(qc);
        (callback || noop)(...args);
      },
    },
  );
};

/** Move a meeting to a new time (bookingId = the meeting being moved). */
export const useRescheduleMeeting = (callback, opts = {}) => {
  const qc = useQueryClient();
  return useMutation(
    async ({ mount, leadId, bookingId, ...rest }) => {
      const res = await axios.post(
        `${basePath(mount, leadId)}/meetings/${bookingId}/reschedule`,
        bookingBody(rest),
      );
      return res.data;
    },
    {
      ...opts,
      onSuccess: (...args) => {
        refreshMeetings(qc);
        (callback || noop)(...args);
      },
    },
  );
};

/**
 * Cancel a meeting still ahead, or mark one missed (action 'cancel' |
 * 'missed'). Refreshes the boards and the Workspace that show it.
 */
export const useMeetingAction = (opts = {}) => {
  const qc = useQueryClient();
  return useMutation(
    async ({ mount, leadId, bookingId, action }) => {
      const res = await axios.post(`${basePath(mount, leadId)}/meetings/${bookingId}/${action}`);
      return res.data;
    },
    {
      ...opts,
      onSuccess: (data, vars) => {
        refreshMeetings(qc);
        opts.onSuccess?.(data, vars);
      },
    },
  );
};
